/*
 * Project: bellcloud
 * File: packages/ui/components/common/Card.tsx
 * Description: Reusable styled card components
 */

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { media } from '@/theme/tokens/breakpoints';
import type { SpacingTokens } from '@/theme/tokens/spacing';
import React from 'react';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

interface CardStyledProps {
  hover?: boolean;
  padding?: keyof SpacingTokens;
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  radius?: 'sm' | 'md' | 'lg';
  backgroundColor?: 'container' | 'background' | 'overlay';
  height?: 'auto' | 'full';
  border?: 'none' | 'default' | 'emphasis';
  layout?: 'flex';
  gap?: keyof SpacingTokens | 0;
  fullWidth?: boolean;
  fullHeight?: boolean;
  flexGrow?: number;
  flexShrink?: number;
  /** 넓은 콘텐츠(테이블 등)가 카드 폭을 넘칠 때 가로 스크롤 허용 (모바일 대응) */
  scrollX?: boolean;
}

interface CardProps extends CardStyledProps {
  title?: string;
  children?: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /** 온보딩 가이드 타겟용 data-tour 속성 */
  'data-tour'?: string;
}

const CardStyled = styled.div<CardStyledProps>`
  padding: ${({ theme, padding }) => (padding ? theme.spacing[padding] : theme.spacing[6])}px;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  height: ${({ fullHeight }) => (fullHeight ? '100%' : 'auto')};
  flex-grow: ${({ flexGrow }) => (flexGrow !== undefined ? flexGrow : 0)};
  flex-shrink: ${({ flexShrink }) => (flexShrink !== undefined ? flexShrink : 1)};
  ${({ scrollX }) =>
    scrollX &&
    css`
      overflow-x: auto;
    `}

  /* Layout */
  ${({ layout = 'flex', theme, gap }) =>
    layout === 'flex' &&
    css`
      display: flex;
      flex-direction: column;
      gap: ${gap !== undefined ? (gap === 0 ? 0 : theme.spacing[gap]) : theme.spacing[4]}px;
    `}

  /* Border Radius - 통일된 값 */
  border-radius: ${({ theme, radius = 'lg' }) => {
    switch (radius) {
      case 'sm':
        return theme.borderRadius.sm;
      case 'md':
        return theme.borderRadius.md;
      case 'lg':
      default:
        return theme.borderRadius.lg;
    }
  }}px;

  /* Border - 통일된 값 (기본적으로 항상 있음) */
  border: ${({ theme, border = 'default' }) => {
    if (border === 'none') {
      return 'none';
    }
    if (border === 'emphasis') {
      return `1px solid ${theme.colors.border.container}`;
    }
    // border === 'default' (기본값)
    return `1px solid ${theme.colors.border.divider}`;
  }};

  /* Box Shadow - 통일된 값 */
  box-shadow: ${({ theme, shadow = 'md' }) => {
    switch (shadow) {
      case 'none':
        return 'none';
      case 'sm':
        return theme.shadows.sm;
      case 'md':
        return theme.shadows.md;
      case 'lg':
      default:
        return theme.shadows.lg;
    }
  }};

  /* Background Color - 통일된 값 */
  background-color: ${({ theme, backgroundColor = 'container' }) => {
    switch (backgroundColor) {
      case 'background':
        return theme.colors.surface.layout.background;
      case 'overlay':
        return theme.colors.surface.layout.overlay;
      case 'container':
      default:
        return theme.colors.surface.layout.container;
    }
  }};

  /* Transition - 통일된 애니메이션 */
  transition: all 0.2s ease;

  /* Hover Effect - 선택적 적용 */
  ${({ hover, theme }) =>
    hover &&
    css`
      cursor: pointer;
      &:hover {
        transform: translateY(-2px);
        box-shadow: ${theme.shadows.lg};
        background-color: ${theme.colors.surface.interactive.hover};
      }
    `}

  ${media.maxSm`
    width: 100%;
    max-width: 100%;
  `}
`;

export const Card: React.FC<CardProps> = ({ title, children, className, onClick, ...props }) => {
  return (
    <CardStyled className={className} onClick={onClick} {...props}>
      {title && <CardTitle>{title}</CardTitle>}
      {children}
    </CardStyled>
  );
};

export const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg}px;
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing[5]}px 0;
`;

export const CardContent = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  line-height: ${({ theme }) => theme.lineHeight.normal};
`;

export const CardGrid = styled.div<{
  columns?: number;
  gap?: keyof SpacingTokens;
}>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 3}, 1fr);
  gap: ${({ theme, gap }) => (gap ? theme.spacing[gap] : theme.spacing[7])}px;

  ${media.maxXl`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.maxSm`
    grid-template-columns: 1fr;
  `}
`;

// Sensor Info Grid - for device configuration cards
export const CardInfoGrid = styled.div<{ sensorCount?: number }>`
  display: grid;
  grid-template-columns: repeat(${p => Math.min(p.sensorCount || 5, 5)}, 1fr);
  width: calc(100% + ${({ theme }) => theme.spacing[6]}px * 2);
  margin-left: -${({ theme }) => theme.spacing[6]}px;
  margin-right: -${({ theme }) => theme.spacing[6]}px;
  column-gap: ${({ theme }) => theme.spacing[6]}px;
  row-gap: 0;

  /* 기본: 좌/우 여백 + divider */
  & > * {
    padding: 0 ${({ theme }) => `${theme.spacing[6]}px`};
    position: relative;
  }

  /* 디바이더를 ::after로 추가 */
  & > *:not(:nth-of-type(${p => Math.min(p.sensorCount || 5, 5)}n))::after {
    content: '';
    position: absolute;
    right: calc(-${({ theme }) => theme.spacing[6]}px / 2);
    top: 0;
    bottom: 0;
    width: 0.5px;
    background: ${({ theme }) => theme.colors.border.divider};
  }

  ${media.maxXl`
    grid-template-columns: repeat(2, 1fr);

    /* 2열에서는 모든 디바이더 숨기고 홀수 번째만 표시 */
    & > *::after {
      display: none;
    }

    & > *:nth-of-type(odd):not(:last-child)::after {
      display: block;
    }
  `}

  ${media.maxSm`
    grid-template-columns: 1fr;

    /* 1열이면 디바이더 제거 */
    & > *::after {
      display: none;
    }
  `}
`;

// General Config Grid - for general device settings
export const CardGeneralGrid = styled.div<{
  sensorCount?: number;
  deviceType?: DeviceType;
}>`
  display: grid;
  ${p =>
    p.deviceType === DeviceType.MINI
      ? `
    /* MINI: 4열 고정, 세로 방향 자동 배치 */
    grid-template-columns: repeat(4, 1fr);
    grid-auto-flow: row;
    grid-template-rows: auto;
  `
      : `
    /* LITE: 센서 개수에 따라 열 개수 결정, 세로 방향 배치 */
    grid-template-columns: repeat(${Math.min(p.sensorCount || 5, 5)}, 1fr);
    grid-auto-flow: column;
    grid-template-rows: repeat(3, auto);
  `}
  width: 100%;
  column-gap: 0;
  row-gap: 0;
  position: relative;

  /* 기본: 좌/우 여백 */
  & > * {
    padding: 0 ${({ theme }) => `${theme.spacing[6]}px`};
    position: relative;
  }

  /* MINI: 첫 번째 열 (4n+1): 왼쪽 패딩 제거 */
  ${p =>
    p.deviceType === DeviceType.MINI
      ? `
    & > *:nth-of-type(4n+1) {
      padding-left: 0;
    }
  `
      : ''}

  /* MINI: 마지막 열 (4n): 오른쪽 패딩 제거 */
  ${p =>
    p.deviceType === DeviceType.MINI
      ? `
    & > *:nth-of-type(4n) {
      padding-right: 0;
    }
  `
      : ''}

  /* MINI: 디바이더 (마지막 열 제외) */
  ${p =>
    p.deviceType === DeviceType.MINI
      ? `
    & > *:not(:nth-of-type(4n))::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 0.5px;
      background: ${p.theme.colors.border.divider};
    }
  `
      : ''}

  /* LITE: 첫 번째 열: 왼쪽 패딩 제거 */
  ${p => {
    if (p.deviceType === DeviceType.LITE) {
      const rows = 3;
      let selectors = [];
      for (let i = 1; i <= rows; i++) {
        selectors.push(`& > *:nth-of-type(${i})`);
      }
      return `${selectors.join(', ')} { padding-left: 0; }`;
    }
    return '';
  }}

  /* LITE: 마지막 열: 오른쪽 패딩 제거 */
  ${p => {
    if (p.deviceType === DeviceType.LITE) {
      const rows = 3;
      const cols = Math.min(p.sensorCount || 5, 5);
      let selectors = [];
      for (let i = cols * rows - rows + 1; i <= cols * rows; i++) {
        selectors.push(`& > *:nth-of-type(${i})`);
      }
      return `${selectors.join(', ')} { padding-right: 0; }`;
    }
    return '';
  }}

  /* LITE: 디바이더 (마지막 열 제외) */
  ${p => {
    if (p.deviceType === DeviceType.LITE) {
      const rows = 3;
      const cols = Math.min(p.sensorCount || 5, 5);
      let selectors = [];
      for (let i = cols * rows - rows + 1; i <= cols * rows; i++) {
        selectors.push(`:nth-of-type(${i})`);
      }
      return `
        & > *:not(${selectors.join(', ')})::after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 0.5px;
          background: ${p.theme.colors.border.divider};
        }
      `;
    }
    return '';
  }}

  ${media.maxXl`
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: row;
    grid-template-rows: auto;

    /* 첫 번째 열 (홀수): 왼쪽 패딩 제거 */
    & > *:nth-of-type(odd) {
      padding-left: 0;
    }

    /* 두 번째 열 (짝수): 오른쪽 패딩 제거 */
    & > *:nth-of-type(even) {
      padding-right: 0;
    }

    /* 2열에서는 홀수 번째만 디바이더 표시 (마지막 제외) */
    & > *::after {
      display: none;
    }

    & > *:nth-of-type(odd):not(:last-child)::after {
      display: block;
      right: 0;
    }
  `}

  ${media.maxSm`
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    grid-template-rows: auto;

    /* 1열: 모든 패딩 제거 */
    & > * {
      padding-left: 0;
      padding-right: 0;
    }

    /* 1열이면 디바이더 제거 */
    & > *::after {
      display: none;
    }
  `}
`;
